import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations, SUPPORTED_LANGS, SUPPORTED_CURRENCIES } from "@/lib/i18n";

const AppContext = createContext(null);

const LS_LANG = "netovibe.lang";
const LS_CURRENCY = "netovibe.currency";

const detectInitialLang = () => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LS_LANG);
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang && SUPPORTED_LANGS.includes(urlLang)) return urlLang;
  const nav = (window.navigator.language || "en").toLowerCase();
  if (nav.startsWith("de")) return "de";
  return "en";
};

const detectInitialCurrency = () => {
  if (typeof window === "undefined") return "EUR";
  const stored = window.localStorage.getItem(LS_CURRENCY);
  if (stored && SUPPORTED_CURRENCIES.includes(stored)) return stored;
  return "EUR";
};

export const AppProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);
  const [currency, setCurrencyState] = useState(detectInitialCurrency);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(LS_LANG, lang);
  }, [lang]);

  useEffect(() => {
    window.localStorage.setItem(LS_CURRENCY, currency);
  }, [currency]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  const setCurrency = useCallback((next) => {
    if (SUPPORTED_CURRENCIES.includes(next)) setCurrencyState(next);
  }, []);

  const t = useMemo(() => translations[lang] || translations.en, [lang]);

  const formatPrice = useCallback(
    (pkg) => {
      if (!pkg) return "";
      if (currency === "USD") {
        return `$${pkg.priceUSD.toLocaleString(lang === "de" ? "de-DE" : "en-US")}`;
      }
      return `€${pkg.priceEUR.toLocaleString(lang === "de" ? "de-DE" : "en-US")}`;
    },
    [currency, lang],
  );

  const value = useMemo(
    () => ({ lang, setLang, currency, setCurrency, t, formatPrice }),
    [lang, setLang, currency, setCurrency, t, formatPrice],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
